// FO-SOI-3: TypeScript shapes mirroring CSC_CORE/Features/SalesOrders DTOs.
//
// Naming convention mirrors features/quotations/types/quotation.types.ts. The
// wire types (suffix `Dto`) match the backend JSON exactly; the workspace
// `SalesOrderDraft` type adds local-only fields (dirty/saving flags) the FE
// needs for the compose page.

// Status enum — numeric to match backend serialization (JsonStringEnumConverter
// is NOT used elsewhere in this project, so enums cross the wire as integers).
export enum SalesOrderStatus {
  Draft = 0,
  Submitted = 1,
  // 86d3mnuxe — Posting/PostingFailed removed. Posting-in-flight + posting-failed are now OUTBOX states,
  // read via GET /api/outbox/status (fetchOutboxStatus, key salesorder:{id}). The SO stays Draft until the
  // post succeeds (→ Submitted).
}

// SSE event type discriminator — mirrors SalesOrderEventType on the backend.
export enum SalesOrderEventType {
  Created = 0,
  Updated = 1,
  Deleted = 2,
  Archived = 3,
}

export interface SalesOrderEvent {
  eventType: SalesOrderEventType;
  salesOrderId: number;
  status: SalesOrderStatus | null;
  // 86d3mnuxe — outbox posting status wire string ("Posting" | "Submitted" | "Failed") + failure detail.
  // The claim publishes "Posting"; the handler's OnTerminalAsync publishes "Submitted"/"Failed". The FE
  // leaves its optimistic "posting…" on a terminal event + refetches detail from the projection.
  postingStatus: string | null;
  lastError: string | null;
  failureDetailJson: string | null;
  timestamp: string;
}

// ─── Line item shapes ─────────────────────────────────────────────────────────

export interface SalesOrderLineItemDto {
  id: number;
  clientId: string;
  sortOrder: number;
  diameterMm: number | null;
  lengthM: number | null;
  gradeCode: string | null;
  isWeldable: boolean;
  quantity: number | null;
  unit: string;
  weightKg: number | null;
  /** Per-piece weight of the matched SKU; lets the qty cell re-derive weightKg
   *  locally (qty × perPc) without a re-match round-trip (86d3dxnd7). */
  weightPerPcKg: number | null;
  matchStatus: string | null;
  rawDescription: string | null;
  notes: string | null;
  matchedSalesDescription: string | null;
  matchedItemId: string | null;
  priceLevel: string | null;
  basePricePerPc: number | null;
  averagePricePerPc: number | null;
  originalSpecJson: string | null;
  isEdited: boolean;
  promotedFromGrade: number | null;
}

// ─── Sales Order detail (GET /{id}) ──────────────────────────────────────────

export interface SalesOrderDto {
  id: number;
  salesOrderNumber: string;
  status: SalesOrderStatus;

  customerName: string;
  customerId: string | null;
  customerEntityId: string | null;
  headMainOffice: string;
  headMainOfficeCustomerId: string | null;
  projectName: string;
  termsId: string | null;
  termsName: string | null;
  salesRepId: string | null;
  salesRepName: string | null;
  salesCoordId: string | null;
  salesCoordName: string | null;

  linkedFirmOfferInternalId: string | null;
  linkedFirmOfferTranId: string | null;

  customerPoNumber: string | null;
  poExpiryDate: string | null;
  deliveryDueDate: string | null;
  contractExpiryDate: string | null;
  date: string;

  // Shipping address (denormalized + override JSON)
  shippingAddressId: string | null;
  shippingAddresseeName: string | null;
  shippingAddrLine1: string | null;
  shippingAddrLine2: string | null;
  shippingAddrLine3: string | null;
  shippingCityTownId: string | null;
  shippingStateProvinceId: string | null;
  shippingZip: string | null;
  shippingCountryId: string | null;
  shippingAddressOverrideJson: string | null;

  // Deliver/Bill-to company (custbody_csc_do_cust) + billing address (FO-SOI-3)
  doCustCompanyId: string | null;
  doCustCompanyName: string | null;
  billingAddressId: string | null;
  billingAddresseeName: string | null;
  billingAddrLine1: string | null;
  billingAddrLine2: string | null;
  billingAddrLine3: string | null;
  billingCityTownId: string | null;
  billingStateProvinceId: string | null;
  billingZip: string | null;
  billingCountryId: string | null;
  billingAddressOverrideJson: string | null;

  scheduleId: string | null;
  scheduleName: string | null;
  authorizedReceivers: string | null;
  otherInstructions: string | null;
  soRemarks: string | null;
  subPoOrderlistLink: string | null;
  invoiceToLetter: string | null;
  officeContact1: string | null;
  contactNumber1: string | null;
  officeContact2: string | null;
  contactNumber2: string | null;
  officeContact3: string | null;
  contactNumber3: string | null;
  isTrailer: boolean;
  isMf: boolean;
  isPpeRequired: boolean;
  isPermitRequired: boolean;
  isPriority: boolean;
  isSpotOrder: boolean;
  isReprint: boolean;
  waitingForOfficialPo: boolean;
  actualFreightPerMt: number | null;
  isDiscountedFreight: boolean;
  discountDetails: string | null;
  reasonForClosing: string | null;
  subagentId: string | null;
  waivedFreight: string;
  /** custbody_so_separatebilling: 1=YES, 2=NO, 3=FREIGHT INCORPORATED (default "2").
   *  Sourced from the Deliver/Bill-to company (86d3addwy). */
  separateBilling: string;
  /** DOP-internal freight-line gate. null = unconfirmed / use the billing-type default
   *  (Separate Billing→include, Freight Incorporated→omit). */
  includeFreightLine: boolean | null;

  // Document Alerts (editable; default from customer)
  alertInvoiceECopy: boolean;
  alertManualComputation: boolean;
  alertMc2Copies: boolean;
  alertDrPicture: boolean;
  alertDrAttachment: boolean;
  alertBrnTraceability: boolean;

  // Pricing carry-over snapshot (read-only display)
  grade230Markup: number | null;
  grade275Markup: number | null;
  grade415Markup: number | null;
  grade520Markup: number | null;
  grade550Markup: number | null;
  overallPricePerKg: number | null;
  pricePerKg230G33: number | null;
  pricePerKg280G40Stv: number | null;
  pricePerKg420G60Stv: number | null;
  pricePerKg520G75Stv: number | null;
  pricePerKg550G80Stv: number | null;
  totalAuthorizedKg: number | null;
  // 86d3c2wd1: frozen rebar VAT rate for the read-only Pricing tab (no live FO on view).
  rebarTaxRate: number | null;
  // 86d3cwpzv: frozen FO freight-per-ton rate for the read-only Freight & Order Total block.
  freightChargePerTon: number | null;
  perGradeBalanceKgJson: string | null;

  extractionSource: string;
  extractionText: string | null;
  extractionFileName: string | null;
  extractionStatus: number;

  netSuiteTranId: string | null;
  netSuiteInternalId: string | null;
  netSuiteUrl: string | null;
  postedAt: string | null;
  postedById: number | null;
  postedByName: string | null;
  preparedById: number | null;
  preparedByName: string | null;
  submittedSnapshotJson: string | null;
  perGradeRequestedKgJson: string | null;

  // 86d3mnuxe — the async posting-lifecycle fields moved to the outbox. Posting status + lastError +
  // failureDetailJson (the drift detail) come from GET /api/outbox/status now, not the SO DTO.

  createdAt: string;
  createdById: number;
  createdByName: string | null;
  updatedAt: string | null;
  isArchived: boolean;
  archivedAt: string | null;

  lineItems: SalesOrderLineItemDto[];
}

// ─── List item shape (GET /paged + GET /{id}/row for SSE splicing) ──────────

export interface SalesOrderListItemDto {
  id: number;
  salesOrderNumber: string;
  status: SalesOrderStatus;
  customerName: string;
  projectName: string;
  linkedFirmOfferTranId: string | null;
  netSuiteTranId: string | null;
  deliveryDueDate: string | null;
  createdAt: string;
  updatedAt: string | null;
  postedAt: string | null;
  createdByName: string | null;
  preparedByName: string | null;
  isArchived: boolean;
}

export interface SalesOrderListParams {
  // 86d3mnuxe: drafts tab = [Draft] (posting/failed are outbox states now, not entity statuses);
  // submitted = [Submitted]. Serialized as repeated ?statuses=… (see salesOrderService).
  statuses?: SalesOrderStatus[];
  search?: string;
  page?: number;
  pageSize?: number;
  sortBy?: string;
  sortDesc?: boolean;
  includeArchived?: boolean;
}

// ─── Firm Offer picker shapes ────────────────────────────────────────────────

export interface NsRef {
  id: string;
  refName: string | null;
}

export interface FirmOfferSummaryDto {
  internalId: string;
  tranId: string;
  customerId: string;
  customerName: string;
  projectName: string | null;
  balanceKg: number;
  totalAuthorizedKg: number;
  salesCoordinator: NsRef | null;
  salesRep: NsRef | null;
}

export interface FirmOfferGradePriceDto {
  markupPct: number;
  pricePerKg: number;
}

export interface FirmOfferPricingDto {
  overallPricePerKg: number | null;
  perGrade: {
    grade230: FirmOfferGradePriceDto;
    grade280: FirmOfferGradePriceDto;
    grade420: FirmOfferGradePriceDto;
    grade520: FirmOfferGradePriceDto;
    grade550: FirmOfferGradePriceDto;
  };
  // Single uniform rebar VAT rate (e.g. 0.12; 0 for a zero-rated customer) from the FO
  // customer's live Customer.TaxRate — what NetSuite re-derives each SO line's tax from at
  // save. Null when the customer isn't in the synced table → fall back to DEFAULT_REBAR_VAT.
  rebarTaxRate: number | null;
}

export interface FirmOfferDetailDto {
  internalId: string;
  tranId: string;
  customer: NsRef;
  headMainOffice: NsRef | null;
  projectName: string | null;
  customerPoNumber: string | null;
  contractExpiryDate: string | null;
  terms: NsRef | null;
  salesRep: NsRef | null;
  salesCoordinator: NsRef | null;
  memo: string | null;
  freightChargePerTon: number | null;
  waivedFreight: NsRef | null;
  poLinkUrl: string | null;
  form2303LinkUrl: string | null;
  pricing: FirmOfferPricingDto;
  perGradeBalanceKg: Record<string, number>;
  // Per-grade NetSuite price-level id read from the FO's grade lines (PNS label ->
  // "1"=Base / "2"=Average). Drives the SO line's default price level. A grade with
  // no priced FO line is absent.
  perGradePriceLevel: Record<string, string>;
  totalAuthorizedKg: number;
  balanceKg: number;
  approvalStatus: NsRef | null;
}

// ─── Customer address book ───────────────────────────────────────────────────

export interface AddressDto {
  internalId: string;
  label: string | null;
  addressee: string | null;
  addrText: string | null;
  countryId: string | null;
  countryName: string | null;
  defaultBilling: boolean;
  defaultShipping: boolean;
  isResidential: boolean;
  // 86d405z28 — PROVINCE (Y/N) as stored on the NetSuite address (customlist818):
  // "1" YES / "2" NO / "3" N/A. Null on the many legacy addresses that predate the field.
  provinceYn: string | null;
  // True when the server recognised this entry as one of the customer's PICK-UP records.
  // The MATCHING is server-side (label first, address text as fallback) — never re-derive it
  // here. Decide by count: exactly 1 → the pick-up address; 0 → offer to create; 2+ → ambiguous,
  // leave them in the saved list and do NOT offer to create (a duplicate cannot be removed).
  isPickupCandidate: boolean;
}

export const PROVINCE_YN = {
  YES: '1',
  NO: '2',
  NA: '3',
} as const;

export type ProvinceYn = (typeof PROVINCE_YN)[keyof typeof PROVINCE_YN];

export const PROVINCE_YN_LABEL: Record<string, string> = {
  '1': 'YES',
  '2': 'NO',
  '3': 'N/A',
};

// 86d405z28 — structured. The server assembles NetSuite's free-form `addrText` from the
// address lines; do not concatenate here.
export interface AppendAddressRequest {
  label?: string;
  addressee: string;
  addr1: string;
  addr2?: string | null;
  addr3?: string | null;
  city?: string | null;
  zip?: string | null;
  countryId: string;
  // REQUIRED — NetSuite refuses to save an address without it.
  provinceYn: ProvinceYn;
  defaultBilling?: boolean;
  defaultShipping?: boolean;
}

// ─── Reference data ──────────────────────────────────────────────────────────

export interface ReferenceDataDto {
  id: string;
  name: string;
}

// 86d39ekrd — SO-owned record-type discriminators for the master-data force-sync
// endpoint (must match CSC_CORE MasterDataRecordTypes). SO owns its own copy
// rather than importing from the quotation-intake feature.
export const SO_REFERENCE_RECORD_TYPES = {
  CityTown: 'customrecord_citytown',
  StateProvince: 'customrecord_so_stateprovince',
  Schedule: 'customlist_schedule',
} as const;

export type SoReferenceRecordType =
  (typeof SO_REFERENCE_RECORD_TYPES)[keyof typeof SO_REFERENCE_RECORD_TYPES];

// Mirrors CSC_CORE CacheHealthDto (GET /api/admin/cache/health).
export interface CacheHealthDto {
  recordType: string;
  lastRefreshedAt: string | null;
  ageSeconds: number | null;
  rowCount: number;
}

// ─── Deliver/Bill-to company hierarchy + profile (FO-SOI-3) ──────────────────

export interface CustomerHierarchyCompany {
  id: string;
  name: string;
  isParent: boolean;
}

export interface CustomerHierarchy {
  rootId: string;
  companies: CustomerHierarchyCompany[];
}

export interface CustomerDocumentAlerts {
  invoiceECopy: boolean;
  manualComputation: boolean;
  mc2Copies: boolean;
  drPicture: boolean;
  drAttachment: boolean;
  brnTraceability: boolean;
}

export interface CustomerCompanyProfile {
  companyId: string;
  termsId: string | null;
  termsName: string | null;
  taxNum: string | null;
  /** custentity_cust_separatefreightyn — NetSuite list id: 1=N/A, 2=Incorporate on
   *  Price, 3=Separate Billing. Seeds the SO separateBilling value + freight-line
   *  prompt (86d3addwy). */
  separateFreightYn: string | null;
  alerts: CustomerDocumentAlerts;
}

// ─── Save-draft request (POST /api/sales-orders/draft) ──────────────────────

export interface SaveSalesOrderDraftLineItem {
  clientId: string;
  sortOrder: number;
  diameterMm: number | null;
  lengthM: number | null;
  gradeCode: string | null;
  isWeldable: boolean;
  quantity: number | null;
  unit: string | null;
  weightKg: number | null;
  matchStatus: string | null;
  rawDescription: string | null;
  notes: string | null;
  matchedSalesDescription: string | null;
  matchedItemId: string | null;
  priceLevel: string | null;
  basePricePerPc: number | null;
  averagePricePerPc: number | null;
  originalSpecJson: string | null;
  isEdited: boolean;
  promotedFromGrade: number | null;
}

export interface SaveSalesOrderDraftRequest {
  id?: number | null;
  linkedFirmOfferInternalId: string | null;
  linkedFirmOfferTranId: string | null;
  customerName: string | null;
  customerId: string | null;
  customerEntityId: string | null;
  headMainOffice: string | null;
  headMainOfficeCustomerId: string | null;
  projectName: string | null;
  termsId: string | null;
  termsName: string | null;
  salesRepId: string | null;
  salesRepName: string | null;
  salesCoordId: string | null;
  salesCoordName: string | null;
  customerPoNumber: string | null;
  poExpiryDate: string | null;
  deliveryDueDate: string | null;
  contractExpiryDate: string | null;
  date: string | null;
  shippingAddressId: string | null;
  shippingAddresseeName: string | null;
  shippingAddrLine1: string | null;
  shippingAddrLine2: string | null;
  shippingAddrLine3: string | null;
  shippingCityTownId: string | null;
  shippingStateProvinceId: string | null;
  shippingZip: string | null;
  shippingCountryId: string | null;
  shippingAddressOverrideJson: string | null;
  doCustCompanyId: string | null;
  doCustCompanyName: string | null;
  billingAddressId: string | null;
  billingAddresseeName: string | null;
  billingAddrLine1: string | null;
  billingAddrLine2: string | null;
  billingAddrLine3: string | null;
  billingCityTownId: string | null;
  billingStateProvinceId: string | null;
  billingZip: string | null;
  billingCountryId: string | null;
  billingAddressOverrideJson: string | null;
  scheduleId: string | null;
  scheduleName: string | null;
  authorizedReceivers: string | null;
  otherInstructions: string | null;
  soRemarks: string | null;
  subPoOrderlistLink: string | null;
  invoiceToLetter: string | null;
  officeContact1: string | null;
  contactNumber1: string | null;
  officeContact2: string | null;
  contactNumber2: string | null;
  officeContact3: string | null;
  contactNumber3: string | null;
  isTrailer: boolean;
  isMf: boolean;
  isPpeRequired: boolean;
  isPermitRequired: boolean;
  isPriority: boolean;
  isSpotOrder: boolean;
  isReprint: boolean;
  waitingForOfficialPo: boolean;
  actualFreightPerMt: number | null;
  isDiscountedFreight: boolean;
  discountDetails: string | null;
  reasonForClosing: string | null;
  subagentId: string | null;
  waivedFreight: string | null;
  separateBilling: string | null;
  includeFreightLine: boolean | null;
  alertInvoiceECopy: boolean;
  alertManualComputation: boolean;
  alertMc2Copies: boolean;
  alertDrPicture: boolean;
  alertDrAttachment: boolean;
  alertBrnTraceability: boolean;
  grade230Markup: number | null;
  grade275Markup: number | null;
  grade415Markup: number | null;
  grade520Markup: number | null;
  grade550Markup: number | null;
  overallPricePerKg: number | null;
  pricePerKg230G33: number | null;
  pricePerKg280G40Stv: number | null;
  pricePerKg420G60Stv: number | null;
  pricePerKg520G75Stv: number | null;
  pricePerKg550G80Stv: number | null;
  totalAuthorizedKg: number | null;
  rebarTaxRate: number | null;
  freightChargePerTon: number | null;
  perGradeBalanceKgJson: string | null;
  extractionSource: string | null;
  extractionText: string | null;
  extractionFileName: string | null;
  lineItems: SaveSalesOrderDraftLineItem[];
}

// ─── Submit request (POST /api/sales-orders/{id}/submit) ─────────────────────

export interface SubmitSalesOrderItem {
  matchedItemId: string;
  quantity: number;
  itemCode: string | null;
  gradeCode: string | null;
  // NS price level ("1"=Base, "2"=Average). Required at submit — drives the line rate.
  priceLevel: string | null;
}

export interface CustomAddressInput {
  addressee: string;
  addrLine1: string;
  addrLine2: string | null;
  addrLine3: string | null;
  cityTownId: string | null;
  stateProvinceId: string | null;
  zip: string | null;
  countryId: string;
  saveToCustomer: boolean;
}

export interface ShippingAddressInput {
  savedAddressId: string | null;
  // 86d3c2wd1: resolved address text for a saved pick, sent at submit so the backend freezes
  // it into the SO denorm columns (saved picks otherwise store only the id).
  savedAddressText?: string | null;
  custom: CustomAddressInput | null;
}

export interface SubmitSalesOrderRequest {
  salesOrderId: number;
  linkedFirmOfferInternalId: string;
  items: SubmitSalesOrderItem[];
  customerPoNumber: string | null;
  poExpiryDate: string | null;
  deliveryDueDate: string | null;
  stateProvinceId: string;
  cityTownId: string;
  waivedFreight: string | null;
  separateBilling: string | null;
  includeFreightLine: boolean | null;
  shippingAddress: ShippingAddressInput;
  doCustCompanyId: string | null;
  doCustCompanyName: string | null;
  billingAddress: ShippingAddressInput | null;
  termsId: string | null;
  termsName: string | null;
  projectName: string;
  alertInvoiceECopy: boolean;
  alertManualComputation: boolean;
  alertMc2Copies: boolean;
  alertDrPicture: boolean;
  alertDrAttachment: boolean;
  alertBrnTraceability: boolean;
  scheduleId: string | null;
  authorizedReceivers: string | null;
  otherInstructions: string | null;
  soRemarks: string | null;
  subPoOrderlistLink: string | null;
  invoiceToLetter: string | null;
  officeContact1: string | null;
  contactNumber1: string | null;
  officeContact2: string | null;
  contactNumber2: string | null;
  officeContact3: string | null;
  contactNumber3: string | null;
  isTrailer: boolean;
  isMf: boolean;
  isPpeRequired: boolean;
  isPermitRequired: boolean;
  isPriority: boolean;
  isSpotOrder: boolean;
  isReprint: boolean;
  waitingForOfficialPo: boolean;
  /** Coordinator-editable freight rate per ton (VAT-incl) that DRIVES the freight charge
   *  (86d3fteqa). Wins over the live-FO re-read at submit. */
  freightChargePerTon: number | null;
  /** Informational only (86d3fteqa) — does not feed any computation. */
  actualFreightPerMt: number | null;
  isDiscountedFreight: boolean;
  discountDetails: string | null;
  reasonForClosing: string | null;
  subagentId: string | null;
  date: string | null;
}

export interface SalesOrderCreatedDto {
  salesOrderId: number;
  salesOrderNumber: string;
  netSuiteTranId: string | null;
  netSuiteInternalId: string;
  netSuiteUrl: string;
  postedAt: string;
  postedByName: string | null;
}

// ─── Compose-page draft state ────────────────────────────────────────────────

export interface SalesOrderDraft extends Omit<SalesOrderDto, 'id'> {
  // Local-only draft state. `serverId` is null until the first save returns
  // a row id; afterwards every subsequent save targets that id.
  localId: string;
  serverId: number | null;

  // Linked-FO snapshot (populated when the user picks an FO).
  linkedFirmOffer: FirmOfferDetailDto | null;

  // Per-customer address book — fetched when the FO is picked.
  addressBook: AddressDto[];

  // Compose-page UX state
  isDirty: boolean;
  isSaving: boolean;
  saveError: string | null;
  lastSavedAt: string | null;

  // Reopen-eligibility banner (shown when a previously-saved draft is opened
  // and the linked FO is no longer eligible).
  eligibilityBanner: string | null;
}

// 86d3gdj5q — one-time "Correct Price" tool result (POST /sales-orders/{id}/correct-price).
// Mirrors CSC_CORE SalesOrderPriceCorrectionResultDto.
export interface SalesOrderPriceCorrectionLine {
  line: number;
  itemId: string | null;
  quantity: number;
  oldRate: number;
  newRate: number;
  oldGross: number;
  newGross: number;
  changed: boolean;
  verified: boolean;
}

export interface SalesOrderPriceCorrectionResult {
  salesOrderId: number;
  salesOrderNumber: string | null;
  netSuiteTranId: string | null;
  // True only when NetSuite accepted the writes AND read-back verified.
  success: boolean;
  // True when at least one line changed (vs. an already-correct no-op).
  corrected: boolean;
  message: string;
  oldTotal: number | null;
  newTotal: number | null;
  // NetSuite lines DOP didn't submit (added directly in NetSuite) — left untouched.
  skippedNetSuiteLines: number;
  lines: SalesOrderPriceCorrectionLine[];
}
